/**
 * Usuários (admin) — CRUD completo sobre `/users`. Todo mundo aqui compartilha
 * o mesmo catálogo e o mesmo histórico de propostas: não há "contas" isoladas
 * por vendedor. O perfil de cada pessoa é o "De" que aparece como vendedor
 * nas propostas que ela gerar — por isso nome, e-mail e celular importam.
 *
 * A rota já é bloqueada para quem não é admin em `App.tsx` (`<AdminOnly>`),
 * mas a tela ainda trata um 403 vindo do servidor sem quebrar (ex.: sessão
 * rebaixada em outra aba).
 */
import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers } from '@/hooks/useAdmin';
import { ApiError } from '@/lib/api';
import type { Role, User } from '@/lib/types';
import { fmtDate, maskPhone } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import './Admin.css';

const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administrador',
  member: 'Equipe',
};

type ConfirmAction = { type: 'delete' | 'deactivate'; user: User };

export function Usuarios() {
  const { user: me } = useAuth();
  const toast = useToast();
  const usersQuery = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [modalUser, setModalUser] = useState<User | 'new' | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  if (usersQuery.isError && usersQuery.error instanceof ApiError && usersQuery.error.status === 403) {
    return (
      <>
        <PageHeader title="Usuários" />
        <div className="restricted stack">
          <p className="empty-title display">Área restrita</p>
          <p className="muted">Esta seção é só para administradores.</p>
        </div>
      </>
    );
  }

  async function handleToggleActive(user: User) {
    try {
      await updateUser.mutateAsync({ id: user.id, input: { active: !user.active } });
      toast.success(user.active ? `${user.name} foi desativado.` : `${user.name} foi reativado.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível atualizar agora.');
    }
  }

  async function handleConfirm() {
    if (!confirmAction) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.type === 'delete') {
        await deleteUser.mutateAsync(confirmAction.user.id);
        toast.success(`${confirmAction.user.name} foi excluído.`);
      } else {
        await updateUser.mutateAsync({ id: confirmAction.user.id, input: { active: false } });
        toast.success(`${confirmAction.user.name} foi desativado.`);
      }
      setConfirmAction(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível concluir agora.');
    } finally {
      setConfirmBusy(false);
    }
  }

  const columns: DataTableColumn<User>[] = [
    {
      key: 'name',
      header: 'Nome',
      render: (row) => (
        <span className="row">
          {row.name}
          {me?.id === row.id ? <Badge variant="neutral">Você</Badge> : null}
        </span>
      ),
    },
    { key: 'email', header: 'E-mail', render: (row) => row.email },
    { key: 'phone', header: 'Celular', render: (row) => (row.phone ? maskPhone(row.phone) : '—') },
    {
      key: 'role',
      header: 'Papel',
      render: (row) => (
        <Badge variant={row.role === 'admin' ? 'accent' : 'neutral'}>{ROLE_LABEL[row.role]}</Badge>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => <Badge variant={row.active ? 'positive' : 'neutral'}>{row.active ? 'Ativo' : 'Inativo'}</Badge>,
    },
    {
      key: 'createdAt',
      header: 'Criado em',
      render: (row) => <span className="mono">{fmtDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (row) => (
        <div className="row row-end row-wrap">
          <Button size="sm" variant="ghost" onClick={() => setModalUser(row)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleToggleActive(row)}>
            {row.active ? 'Desativar' : 'Ativar'}
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={me?.id === row.id}
            onClick={() => setConfirmAction({ type: 'delete', user: row })}
          >
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Usuários"
        meta="Todo mundo aqui compartilha o mesmo catálogo e o mesmo histórico de propostas — o perfil de cada pessoa é o que aparece como vendedor nas propostas dela."
        actions={<Button onClick={() => setModalUser('new')}>Novo usuário</Button>}
      />

      <Panel>
        <DataTable
          columns={columns}
          rows={usersQuery.data ?? []}
          rowKey={(row) => row.id}
          loading={usersQuery.isLoading}
          empty={
            <EmptyState
              title="Nenhum usuário cadastrado."
              description="Crie a primeira conta da equipe."
              action={<Button onClick={() => setModalUser('new')}>Novo usuário</Button>}
            />
          }
        />
      </Panel>

      {modalUser ? (
        <UserFormModal
          initial={modalUser === 'new' ? null : modalUser}
          onClose={() => setModalUser(null)}
          onCreate={(input) => createUser.mutateAsync(input)}
          onUpdate={(id, input) => updateUser.mutateAsync({ id, input })}
        />
      ) : null}

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmAction?.type === 'delete' ? 'Excluir usuário' : 'Desativar usuário'}
        tone="danger"
        confirmLabel={confirmAction?.type === 'delete' ? 'Excluir' : 'Desativar'}
        loading={confirmBusy}
        message={
          confirmAction?.type === 'delete' ? (
            <>
              Excluir <strong>{confirmAction.user.name}</strong>? As propostas já emitidas por essa pessoa
              são mantidas — apenas o vínculo com a conta é removido. Prefira desativar se quiser manter o
              histórico de acesso.
            </>
          ) : confirmAction ? (
            <>
              Desativar <strong>{confirmAction.user.name}</strong>? A pessoa não conseguirá mais entrar; o
              histórico de propostas dela é mantido e pode ser revertido a qualquer momento.
            </>
          ) : (
            ''
          )
        }
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </>
  );
}

function UserFormModal({
  initial,
  onClose,
  onCreate,
  onUpdate,
}: {
  initial: User | null;
  onClose: () => void;
  onCreate: (input: { name: string; email: string; password: string; phone?: string; role?: Role }) => Promise<User>;
  onUpdate: (
    id: number,
    input: { name?: string; email?: string; phone?: string; role?: Role; active?: boolean; password?: string }
  ) => Promise<User>;
}) {
  const toast = useToast();
  const isEdit = initial !== null;
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'member');
  const [active, setActive] = useState(initial?.active ?? true);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    if (!isEdit && !password) {
      setFieldErrors({ password: 'Informe uma senha.' });
      return;
    }
    if (password && password.length < 8) {
      setFieldErrors({ password: 'A senha deve ter pelo menos 8 caracteres.' });
      return;
    }

    setSaving(true);
    try {
      if (initial) {
        await onUpdate(initial.id, {
          name,
          email,
          phone: phone || undefined,
          role,
          active,
          password: password || undefined,
        });
        toast.success('Usuário atualizado.');
      } else {
        await onCreate({ name, email, password, phone: phone || undefined, role });
        toast.success('Usuário criado.');
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      title={isEdit ? 'Editar usuário' : 'Novo usuário'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="user-form" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id="user-form" className="stack" onSubmit={handleSubmit} noValidate>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Nome"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
          />
          <Field
            label="Celular"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(maskPhone(e.target.value))}
            error={fieldErrors.phone}
          />
        </div>
        <Field
          label="E-mail"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <div className="field-grid">
          <Field
            as="select"
            label="Papel"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="member">Equipe</option>
            <option value="admin">Administrador</option>
          </Field>
          <Field
            label={isEdit ? 'Definir nova senha' : 'Senha'}
            type="password"
            required={!isEdit}
            autoComplete="new-password"
            hint={isEdit ? 'Deixe em branco para manter a senha atual. Mínimo de 8 caracteres.' : 'Mínimo de 8 caracteres.'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
          />
        </div>
        {isEdit ? (
          <label className="checkbox">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Ativo — pode entrar no sistema
          </label>
        ) : null}
      </form>
    </Modal>
  );
}
